import React, { Suspense } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import type { SiteNameKnowledgeQuery } from './__generated__/SiteNameKnowledgeQuery.graphql.ts';

type SiteNameProps = {
	cloudId: string;
};

const SiteNameInner = ({ cloudId }: SiteNameProps): React.JSX.Element | null => {
	const data = useLazyLoadQuery<SiteNameKnowledgeQuery>(
		graphql`
			query SiteNameKnowledgeQuery($cloudIds: [ID!]) {
				tenantContexts(cloudIds: $cloudIds) {
					cloudId
					cloudUrl
				}
			}
		`,
		{ cloudIds: [cloudId] },
	);

	const tenantContext = data.tenantContexts?.find((tc) => tc?.cloudId === cloudId);
	const cloudUrl = tenantContext?.cloudUrl ? String(tenantContext.cloudUrl) : '';

	if (!cloudUrl) {
		return null;
	}

	const siteName = cloudUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

	return (
		<Inline space="space.100" alignBlock="center">
			<OfficeBuildingIcon label="" color={token('color.icon.subtle')} />
			<Text size="small" color="color.text.subtle" weight="medium">
				{siteName}
			</Text>
		</Inline>
	);
};

/**
 * Resolves a site name from a cloudId via `tenantContexts` and renders it with a site icon.
 * Self-contained: owns its own Suspense boundary (Skeleton fallback while the tenant lookup resolves).
 */
export const SiteName = ({ cloudId }: SiteNameProps): React.JSX.Element => {
	return (
		<Suspense fallback={<Skeleton height={16} width={120} isShimmering />}>
			<SiteNameInner cloudId={cloudId} />
		</Suspense>
	);
};
